import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { v4 as uuidv4 } from 'uuid';
import { getLocalTimeZone, now } from '@internationalized/date';
import {
  getLocalStorageValue,
  prefixStorageKey,
} from '@antimatterhq/ui/utils/localStorage';
import { destr } from 'destr';
import type { QueryTab, QueryVersion, RecentQuery } from '../../types/query';
import {
  deserializeTabsFromStorage,
  pruneVersions,
  serializeTabsForStorage,
} from '@/lib/query/serialization';
import { QueryResultsTableData } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultsTableData';

// Base storage key constants (without ASL prefix)
const RECENT_QUERIES_BASE_KEY = 'query-recent-queries';
const QUERY_TAB_IDS_BASE_KEY = 'query-tab-ids';
const QUERY_ACTIVE_TAB_BASE_KEY = 'query-active-tab';
const QUERY_TABS_DATA_BASE_KEY = 'query-tabs-data';

// Get storage key with instance ID prefix
const getStorageKey = (baseKey: string) => {
  const instanceId =
    getLocalStorageValue<string>(prefixStorageKey('instanceID')) ?? '';
  return instanceId ? `${instanceId}/${baseKey}` : baseKey;
};

// Internal atoms to hold the actual values
const internalRecentQueriesAtom = atom<RecentQuery[]>([]);
const internalQueryTabIdsAtom = atom<string[]>([]);
const internalActiveTabIdAtom = atom<string | null>(null);

// Create atoms with dynamic storage keys
export const recentQueriesAtom = atom<RecentQuery[], [RecentQuery[]], void>(
  (get) => {
    // Return the internal atom value
    return get(internalRecentQueriesAtom);
  },
  (_get, set, newValue: RecentQuery[]) => {
    // Update internal atom and persist to localStorage
    set(internalRecentQueriesAtom, newValue);
    localStorage.setItem(
      prefixStorageKey(getStorageKey(RECENT_QUERIES_BASE_KEY)),
      JSON.stringify(newValue),
    );
  },
);

/**
 * Just an ordered list of tab IDs, kept separate so we can restore tabs in
 * the same order they were opened.
 */
export const queryTabIdsAtom = atom<string[], [string[]], void>(
  (get) => {
    return get(internalQueryTabIdsAtom);
  },
  (_get, set, newValue: string[]) => {
    set(internalQueryTabIdsAtom, newValue);
    localStorage.setItem(
      prefixStorageKey(getStorageKey(QUERY_TAB_IDS_BASE_KEY)),
      JSON.stringify(newValue),
    );
  },
);

/** ID of the tab the user is currently looking at. */
export const activeQueryTabIdAtom = atom<string | null, [string | null], void>(
  (get) => {
    return get(internalActiveTabIdAtom);
  },
  (_get, set, newValue: string | null) => {
    set(internalActiveTabIdAtom, newValue);
    localStorage.setItem(
      prefixStorageKey(getStorageKey(QUERY_ACTIVE_TAB_BASE_KEY)),
      JSON.stringify(newValue),
    );
  },
);

/**
 * atomFamily uses the ID as a key to create separate atom instances for each tab.
 * The ID parameter isn't used within the factory function itself, only by the atomFamily.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const queryTabAtomFamily = atomFamily((_id: string) =>
  atom<QueryTab | null>(null),
);

/**
 * Convenience read-only atom that gives you the *whole* active tab object, or
 * `null` when nothing’s open.
 */
export const activeQueryTabAtom = atom((get) => {
  const activeId = get(activeQueryTabIdAtom);
  return activeId ? get(queryTabAtomFamily(activeId)) : null;
});

/** `true` when *any* tab exists (used for the Empty-state vs Tabs decision). */
export const hasOpenQueryTabsAtom = atom(
  (get) => get(queryTabIdsAtom).length > 0,
);

/**
 * Gathers all known tab data from memory. This is the source of truth for
 * persistence. It reactively depends on the list of open tabs and recent
 * queries to ensure it's always up-to-date.
 */
export const allQueryTabsAtom = atom((get) => {
  const openTabIds = get(queryTabIdsAtom);
  const recentQueryIds = get(recentQueriesAtom).map((r) => r.id);
  const allIds = [...new Set([...openTabIds, ...recentQueryIds])];

  return allIds
    .map((id) => get(queryTabAtomFamily(id)))
    .filter((tab): tab is QueryTab => tab !== null);
});

/**
 * Saves all tab data to localStorage. Called when tabs are saved or closed.
 */
export const syncQueryTabsToLocalStateAtom = atom(null, (get) => {
  const allTabs = get(allQueryTabsAtom);
  try {
    localStorage.setItem(
      prefixStorageKey(getStorageKey(QUERY_TABS_DATA_BASE_KEY)),
      serializeTabsForStorage(allTabs),
    );
  } catch (err) {
    console.error('Failed to persist tab data', err);
  }
});

/**
 * Opens a new blank query tab and immediately focuses it.
 * Returns the new tab object for if we ever need it.
 */
export const createNewQueryTabAtom = atom(null, (get, set) => {
  const timezone = getLocalTimeZone();
  const currentDateTime = now(timezone);
  const startOfDay = currentDateTime.set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });

  const name = `New Query ${new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date())}`;

  const newTab: QueryTab = {
    id: uuidv4(),
    name,
    refinements: [],
    sql: '',
    dateRange: { start: startOfDay, end: currentDateTime },
    timezone,
    status: 'idle',
    isLocalDirty: false,
    histogram: {
      timeRanges: [],
      interval: '',
    },
    queriedDataRange: undefined,
  };

  set(queryTabAtomFamily(newTab.id), newTab); // Set data first
  set(queryTabIdsAtom, [...get(queryTabIdsAtom), newTab.id]); // Then add to open tabs
  set(activeQueryTabIdAtom, newTab.id);

  const recent = get(recentQueriesAtom);
  const asRecent: RecentQuery = {
    id: newTab.id,
    name: newTab.name,
    queryId: newTab.id,
    lastOpened: new Date().toISOString(),
    lastModified: new Date().toISOString(),
  };
  // Put newest at the front, drop any previous entry for the same id to avoid duplicates
  set(recentQueriesAtom, [
    asRecent,
    ...recent.filter((r) => r.id !== newTab.id),
  ]);

  // Persist the new tab data
  set(syncQueryTabsToLocalStateAtom);
});

/** Focus a tab by id (no-op if it doesn’t exist). */
export const switchTabAtom = atom(null, (get, set, tabId: string) => {
  if (get(queryTabIdsAtom).includes(tabId)) set(activeQueryTabIdAtom, tabId);
});

/**
 * Write-only atoms, one per tab, so components can call
 * `useSetAtom(updateTabAtomFamily(id))` and patch the tab.
 */
export const updateTabAtomFamily = atomFamily((tabId: string) =>
  atom(null, (get, set, updates: Partial<QueryTab>) => {
    const current = get(queryTabAtomFamily(tabId));
    if (!current) return;

    // Mark the tab as locally dirty and needing remote sync
    set(queryTabAtomFamily(tabId), {
      ...current,
      ...updates,
      isLocalDirty: true,
    });

    // bump `lastModified` in recents list so the UI reflects activity
    const recents = get(recentQueriesAtom);
    const idx = recents.findIndex((r) => r.id === tabId);
    if (idx !== -1) {
      const updated = {
        ...recents[idx],
        ...(updates.name && { name: updates.name }),
        lastModified: new Date().toISOString(),
      };
      set(recentQueriesAtom, [
        ...recents.slice(0, idx),
        updated,
        ...recents.slice(idx + 1),
      ]);
    }
  }),
);

/**
 * Snapshots the tab into its local version history & clears the local dirty flag.
 * Called by the debounced autosave.
 */
export const saveTabLocallyAtom = atom(null, (get, set, tabId: string) => {
  const tab = get(queryTabAtomFamily(tabId));
  if (!tab || !tab.isLocalDirty) return;

  const version: QueryVersion = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    sql: tab.sql,
    refinements: tab.refinements,
    dateRange: tab.dateRange,
    timezone: tab.timezone,
  };

  const nextVersions = pruneVersions([...(tab.versions || []), version], 20);

  set(queryTabAtomFamily(tabId), {
    ...tab,
    versions: nextVersions,
    isLocalDirty: false, // Clear local dirty flag after saving
  });

  // refresh recents entry so "modified" timestamp updates
  const recents = get(recentQueriesAtom);
  const idx = recents.findIndex((r) => r.id === tabId);
  if (idx !== -1) {
    const updated = {
      ...recents[idx],
      lastModified: new Date().toISOString(),
    };
    set(recentQueriesAtom, [
      ...recents.slice(0, idx),
      updated,
      ...recents.slice(idx + 1),
    ]);
  }

  // Persist all tab data after saving
  set(syncQueryTabsToLocalStateAtom);
});

// Track initialization state to prevent overwriting data on first render
export const queryTabsInitializationStateAtom = atom<
  'initializing' | 'initialized' | 'uninitialized'
>('uninitialized');

function isInitializingOrInitialized(
  state: 'initializing' | 'initialized' | 'uninitialized',
) {
  return state === 'initializing' || state === 'initialized';
}

/**
 * Only persists tab IDs and active tab to localStorage. Tab data is saved
 * separately via saveTabAtom when appropriate (autosave or on close).
 */
export const tabPersistenceAtom = atom((get) => {
  // Don't start persisting until we have fully loaded from localStorage first
  if (get(queryTabsInitializationStateAtom) !== 'initialized') {
    return null;
  }

  const openTabIds = get(queryTabIdsAtom);

  try {
    // Only persist the list of open tabs, not the tab data itself
    localStorage.setItem(
      prefixStorageKey(getStorageKey(QUERY_TAB_IDS_BASE_KEY)),
      JSON.stringify(openTabIds),
    );
  } catch (err) {
    console.error('Failed to persist query tab IDs', err);
  }
  return null;
});

/** Reads localStorage on first load and hydrates all tab atoms. */
export const initializeQueryTabsAtom = atom(null, (get, set) => {
  if (isInitializingOrInitialized(get(queryTabsInitializationStateAtom))) {
    return;
  }

  // Mark as initializing immediately to prevent tabPersistenceAtom from
  // writing empty data before we've finished hydrating from localStorage.
  set(queryTabsInitializationStateAtom, 'initializing');

  try {
    // First, load the recent queries
    const recentQueriesRaw = localStorage.getItem(
      prefixStorageKey(getStorageKey(RECENT_QUERIES_BASE_KEY)),
    );

    const recentQueries = recentQueriesRaw
      ? destr<RecentQuery[]>(recentQueriesRaw) || []
      : [];

    set(internalRecentQueriesAtom, recentQueries);

    // Load all tab data
    const tabsDataRaw = localStorage.getItem(
      prefixStorageKey(getStorageKey(QUERY_TABS_DATA_BASE_KEY)),
    );

    const allTabs = tabsDataRaw ? deserializeTabsFromStorage(tabsDataRaw) : [];
    const allTabsMap = new Map(allTabs.map((tab) => [tab.id, tab]));

    // Hydrate the family with all persisted tab data.
    for (const tab of allTabs) {
      set(queryTabAtomFamily(tab.id), tab);
    }

    // Get the list of previously open tabs.
    const tabIdsRaw = localStorage.getItem(
      prefixStorageKey(getStorageKey(QUERY_TAB_IDS_BASE_KEY)),
    );

    const openTabIds = tabIdsRaw ? destr<string[]>(tabIdsRaw) || [] : [];

    // Clean up the list of open tabs, ensuring they all have data.
    const validOpenTabIds = openTabIds.filter((id: string) =>
      allTabsMap.has(id),
    );

    set(internalQueryTabIdsAtom, validOpenTabIds);

    // Get the active tab from localStorage
    const activeTabRaw = localStorage.getItem(
      prefixStorageKey(getStorageKey(QUERY_ACTIVE_TAB_BASE_KEY)),
    );

    const storedActiveId = activeTabRaw
      ? destr<string | null>(activeTabRaw)
      : null;

    const activeId =
      storedActiveId && validOpenTabIds.includes(storedActiveId)
        ? storedActiveId
        : validOpenTabIds[0] || null;

    set(internalActiveTabIdAtom, activeId);

    // Create new tab if there are no open tabs & no recent queries
    if (validOpenTabIds.length === 0 && recentQueries.length === 0) {
      set(createNewQueryTabAtom);
    }

    // Mark as initialized so persistence can start
    set(queryTabsInitializationStateAtom, 'initialized');
  } catch (err) {
    console.error('Failed to restore query tabs', err);
    // Even on error, mark as initialized to allow normal operation
    set(queryTabsInitializationStateAtom, 'initialized');
  }
});

export const cleanupQueryTabsAtom = atom(null, (_get, set) => {
  set(queryTabsInitializationStateAtom, 'uninitialized');
});

/**
 * Closes and forgets a tab, behaves kinda like VS Code where if you close the
 * current tab the focus hops to the neighbour on the left.
 */
export const closeQueryTabAtom = atom(null, (get, set, tabId: string) => {
  const ids = get(queryTabIdsAtom);
  const idx = ids.indexOf(tabId);
  if (idx === -1) return;

  // snapshot one last time before the tab disappears
  set(saveTabLocallyAtom, tabId);

  const newIds = ids.filter((id) => id !== tabId);
  set(queryTabIdsAtom, newIds);
  // The tab's data remains in the tabFamily, so it can be reopened from recents.

  if (get(activeQueryTabIdAtom) === tabId) {
    if (newIds.length === 0) {
      set(activeQueryTabIdAtom, null);
    } else {
      // Focus the tab to the left, or the new last tab if the closed one was last
      const newIndex = Math.max(0, idx - 1);
      set(activeQueryTabIdAtom, newIds[newIndex]);
    }
  }
});

/**
 * Open or focus a tab by ID.
 */
export const openQueryTabAtom = atom(null, (get, set, queryId: string) => {
  // Already open -> just focus
  if (get(queryTabIdsAtom).includes(queryId)) {
    set(activeQueryTabIdAtom, queryId);
    return;
  }

  // If the tab data is in memory (i.e., it was hydrated at startup), open it.
  const existing = get(queryTabAtomFamily(queryId));
  if (existing) {
    set(queryTabIdsAtom, [...get(queryTabIdsAtom), queryId]);
    set(activeQueryTabIdAtom, queryId);

    // refresh lastOpened timestamp
    set(
      recentQueriesAtom,
      get(recentQueriesAtom).map((r) =>
        r.id === queryId ? { ...r, lastOpened: new Date().toISOString() } : r,
      ),
    );
  }
  // If `existing` is null, it means the queryId is invalid or its data was
  // corrupted or lost. We do nothing to avoid creating an empty tab.
});

// Drawer state interface
interface QueryResultsDrawerState {
  isOpen: boolean;
  isExpanded: boolean;
  selectedRowData: QueryResultsTableData | null;
}

/**
 * Non-persistent drawer state atom family based on tab IDs.
 * This state resets when the app loads and doesn't persist to localStorage.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const queryResultsDrawerStateAtomFamily = atomFamily((_tabId: string) =>
  atom<QueryResultsDrawerState>({
    isOpen: false,
    isExpanded: false,
    selectedRowData: null,
  }),
);

/**
 * Update drawer state for a specific tab.
 */
export const updateQueryResultsDrawerStateAtomFamily = atomFamily(
  (tabId: string) =>
    atom(null, (get, set, updates: Partial<QueryResultsDrawerState>) => {
      const current = get(queryResultsDrawerStateAtomFamily(tabId));
      set(queryResultsDrawerStateAtomFamily(tabId), {
        ...current,
        ...updates,
      });
    }),
);
